import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import { ShieldCheck, X, Image as ImageIcon, ListChecks, Calendar, MapPin, Users, Inbox, CheckCircle2, Clock as ClockIcon } from "lucide-react";
import { toast } from "sonner";

const ID_TABS = [
  { key: "submitted", label: "Občanky čekající" },
  { key: "approved", label: "Občanky schválené" },
  { key: "rejected", label: "Občanky zamítnuté" },
];
const EVT_TABS = [
  { key: "pending_review", label: "Akce čekající" },
  { key: "approved", label: "Akce schválené" },
  { key: "rejected", label: "Akce zamítnuté" },
];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("idcards");
  const [idTab, setIdTab] = useState("submitted");
  const [evtTab, setEvtTab] = useState("pending_review");
  const [reqTab, setReqTab] = useState("new");

  if (loading) return <div className="min-h-screen bg-paper grid place-items-center text-slate-500">Načítám…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-7 flex-wrap gap-3">
          <Link to="/" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5" data-testid="admin-back-logo">
            ConnectYou<span className="text-sky-500">.</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Admin panel</span>
        </div>

        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Moderace</h1>

        {/* Section selector */}
        <div className="mt-5 flex gap-2 border-b border-slate-200" data-testid="admin-section-tabs">
          <button
            data-testid="admin-section-idcards"
            onClick={() => setTab("idcards")}
            className={[
              "px-1 pb-3 -mb-px text-sm font-bold border-b-2 transition-colors",
              tab === "idcards" ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700",
            ].join(" ")}
          >
            <ShieldCheck className="w-4 h-4 inline-block mr-1.5" /> Občanky
          </button>
          <button
            data-testid="admin-section-events"
            onClick={() => setTab("events")}
            className={[
              "px-1 pb-3 -mb-px ml-5 text-sm font-bold border-b-2 transition-colors",
              tab === "events" ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700",
            ].join(" ")}
          >
            <ListChecks className="w-4 h-4 inline-block mr-1.5" /> Akce
          </button>
          <button
            data-testid="admin-section-requests"
            onClick={() => setTab("requests")}
            className={[
              "px-1 pb-3 -mb-px ml-5 text-sm font-bold border-b-2 transition-colors",
              tab === "requests" ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700",
            ].join(" ")}
          >
            <Inbox className="w-4 h-4 inline-block mr-1.5" /> Žádosti
          </button>
        </div>

        {tab === "idcards" ? (
          <IdCardsSection currentTab={idTab} setCurrentTab={setIdTab} />
        ) : tab === "events" ? (
          <EventsSection currentTab={evtTab} setCurrentTab={setEvtTab} />
        ) : (
          <RequestsSection currentTab={reqTab} setCurrentTab={setReqTab} />
        )}
      </div>
    </div>
  );
}

function IdCardsSection({ currentTab, setCurrentTab }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [imgUrls, setImgUrls] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/verifications?status=${currentTab}`);
      setItems(data);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [currentTab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => Object.values(imgUrls).forEach(URL.revokeObjectURL), []); // eslint-disable-line

  const loadImage = async (uid) => {
    if (imgUrls[uid]) return;
    try {
      const { data } = await api.get(`/admin/users/${uid}/id-card`, { responseType: "blob" });
      setImgUrls((p) => ({ ...p, [uid]: URL.createObjectURL(data) }));
    } catch {
      toast.error("Nelze načíst občanku");
    }
  };

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/users/${uid}/verify`, { action });
      toast.success(action === "approve" ? "Schváleno" : "Zamítnuto");
      setItems((prev) => prev.filter((p) => p.id !== uid));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  return (
    <>
      <div className="mt-6 flex gap-2 flex-wrap" data-testid="admin-id-tabs">
        {ID_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-id-tab-${t.key}`}
            onClick={() => setCurrentTab(t.key)}
            className={[
              "px-4 py-2 rounded-full text-sm font-bold transition-colors",
              currentTab === t.key ? "bg-sky-500 text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-sky-300",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-id-list">
        {busy ? (
          <div className="text-slate-500 font-medium">Načítám…</div>
        ) : items.length === 0 ? (
          <div data-testid="admin-id-empty" className="text-slate-500 font-medium">Žádní uživatelé v této kategorii.</div>
        ) : (
          items.map((u) => (
            <div key={u.id} data-testid={`admin-row-${u.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-4">
              <div>
                <div className="font-heading text-lg font-bold text-slate-900">{u.name}</div>
                <div className="text-sm text-slate-600 font-medium">{u.email} · věk {u.age}</div>
                <div className="text-xs text-slate-500 mt-0.5">IG: @{u.instagram_handle || "—"}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3 grid place-items-center min-h-[180px]">
                {imgUrls[u.id] ? (
                  <img src={imgUrls[u.id]} alt="občanka" className="max-h-72 rounded-lg" data-testid={`admin-id-img-${u.id}`} />
                ) : (
                  <button
                    data-testid={`admin-load-id-${u.id}`}
                    onClick={() => loadImage(u.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-bold hover:border-sky-300"
                  >
                    <ImageIcon className="w-4 h-4" /> Zobrazit občanku
                  </button>
                )}
              </div>
              {currentTab === "submitted" && (
                <div className="flex gap-2">
                  <button data-testid={`admin-approve-${u.id}`} onClick={() => act(u.id, "approve")} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-colors active:scale-95">
                    <ShieldCheck className="w-4 h-4" /> Schválit
                  </button>
                  <button data-testid={`admin-reject-${u.id}`} onClick={() => act(u.id, "reject")} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition-colors active:scale-95">
                    <X className="w-4 h-4" /> Zamítnout
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
}

function EventsSection({ currentTab, setCurrentTab }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/events?status=${currentTab}`);
      setItems(data);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [currentTab]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/events/${id}/moderate`, { action });
      toast.success(action === "approve" ? "Akce schválena" : "Akce zamítnuta");
      setItems((p) => p.filter((e) => e.id !== id));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const toggleHot = async (id) => {
    try {
      const { data } = await api.post(`/admin/events/${id}/hot`);
      toast.success(data.is_hot ? "Označeno jako 🔥 HOT" : "🔥 HOT odebráno");
      setItems((p) => p.map((e) => e.id === id ? { ...e, is_hot: data.is_hot } : e));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  return (
    <>
      <div className="mt-6 flex gap-2 flex-wrap" data-testid="admin-event-tabs">
        {EVT_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-event-tab-${t.key}`}
            onClick={() => setCurrentTab(t.key)}
            className={[
              "px-4 py-2 rounded-full text-sm font-bold transition-colors",
              currentTab === t.key ? "bg-sky-500 text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-sky-300",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-event-list">
        {busy ? (
          <div className="text-slate-500 font-medium">Načítám…</div>
        ) : items.length === 0 ? (
          <div data-testid="admin-event-empty" className="text-slate-500 font-medium">Žádné akce v této kategorii.</div>
        ) : (
          items.map((e) => (
            <div key={e.id} data-testid={`admin-event-row-${e.id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
              {e.photo_url && <img src={e.photo_url} alt={e.title} className="w-full aspect-[16/9] object-cover" onError={(ev) => ev.currentTarget.style.display = "none"} />}
              <div className="p-5 flex flex-col gap-2.5 flex-1">
                <div className="font-heading text-lg font-bold text-slate-900">{e.title}</div>
                <div className="text-sm text-slate-600 font-medium leading-relaxed">{e.description}</div>
                <div className="text-xs text-slate-500 font-medium flex flex-col gap-1 mt-1">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {e.city}, {e.country}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {e.date_text || e.date}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-3 h-3" /> {e.capacity} lidí · Kdo to vymyslel: {e.organizer}</span>
                </div>
                {e.organizer_note && (
                  <div className="text-xs text-slate-600 italic mt-1 leading-relaxed">&ldquo;{e.organizer_note}&rdquo;</div>
                )}
                {currentTab === "pending_review" && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`admin-event-approve-${e.id}`} onClick={() => act(e.id, "approve")} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-colors active:scale-95">
                      <ShieldCheck className="w-4 h-4" /> Schválit
                    </button>
                    <button data-testid={`admin-event-reject-${e.id}`} onClick={() => act(e.id, "reject")} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition-colors active:scale-95">
                      <X className="w-4 h-4" /> Zamítnout
                    </button>
                  </div>
                )}
                {currentTab === "approved" && (
                  <button
                    data-testid={`admin-event-hot-${e.id}`}
                    onClick={() => toggleHot(e.id)}
                    className={[
                      "mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold transition-colors active:scale-95",
                      e.is_hot
                        ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        : "bg-white border border-amber-200 text-amber-600 hover:bg-amber-50",
                    ].join(" ")}
                  >
                    🔥 {e.is_hot ? "Odebrat HOT" : "Označit jako HOT"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

const REQ_TABS = [
  { key: "new", label: "Nové" },
  { key: "in_progress", label: "V řešení" },
  { key: "resolved", label: "Vyřešené" },
];

const REQ_STATUS_LABEL = {
  new: { cls: "bg-amber-100 text-amber-700", icon: <Inbox className="w-3 h-3" />, label: "Nové" },
  in_progress: { cls: "bg-sky-100 text-sky-700", icon: <ClockIcon className="w-3 h-3" />, label: "V řešení" },
  resolved: { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="w-3 h-3" />, label: "Vyřešeno" },
};

function RequestsSection({ currentTab, setCurrentTab }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/requests?status=${currentTab}`);
      setItems(data);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [currentTab]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      toast.success("Aktualizováno");
      setItems((p) => p.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  return (
    <>
      <div className="mt-6 flex gap-2 flex-wrap" data-testid="admin-req-tabs">
        {REQ_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-req-tab-${t.key}`}
            onClick={() => setCurrentTab(t.key)}
            className={[
              "px-4 py-2 rounded-full text-sm font-bold transition-colors",
              currentTab === t.key ? "bg-sky-500 text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-sky-300",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3" data-testid="admin-req-list">
        {busy ? (
          <div className="text-slate-500 font-medium">Načítám…</div>
        ) : items.length === 0 ? (
          <div data-testid="admin-req-empty" className="text-slate-500 font-medium">Žádné žádosti v této kategorii.</div>
        ) : (
          items.map((r) => {
            const badge = REQ_STATUS_LABEL[r.status] || REQ_STATUS_LABEL.new;
            return (
              <div key={r.id} data-testid={`admin-req-row-${r.id}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">📨</span>
                      <span className="font-heading text-lg font-bold text-slate-900">{r.topic}</span>
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.icon}{badge.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 font-medium mt-0.5">{r.name} · {r.email} · {new Date(r.created_at).toLocaleString("cs-CZ")}</div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-700 font-medium whitespace-pre-wrap leading-relaxed">{r.message}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {r.status !== "in_progress" && (
                    <button data-testid={`admin-req-progress-${r.id}`} onClick={() => setStatus(r.id, "in_progress")} className="px-3 py-1.5 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-bold">Vzít do řešení</button>
                  )}
                  {r.status !== "resolved" && (
                    <button data-testid={`admin-req-resolved-${r.id}`} onClick={() => setStatus(r.id, "resolved")} className="px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold">Označit vyřešeno</button>
                  )}
                  <a href={`mailto:${r.email}?subject=${encodeURIComponent("ConnectYou — " + r.topic)}`} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-sky-300 text-slate-700 text-xs font-bold">Odpovědět e-mailem</a>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

